import React, { useState, useEffect } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { AlertCircle, Shield, ShieldCheck, RefreshCw } from 'lucide-react';

const Captcha = ({ onVerify, error, resetTrigger }) => {
  const { executeRecaptcha, loaded } = useGoogleReCaptcha();
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [token, setToken] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (resetTrigger) {
      setIsVerified(false);
      setToken(null);
    }
  }, [resetTrigger]);

  useEffect(() => {
    // Check if reCAPTCHA is loaded
    if (loaded === false) {
      setLoadError(true);
    } else {
      setLoadError(false);
    }
  }, [loaded]);

  const handleVerify = async () => {
    if (!executeRecaptcha) {
      if (onVerify) onVerify(null, 'reCAPTCHA not loaded');
      return;
    }

    setIsVerifying(true);
    
    try {
      const token = await executeRecaptcha('auth_form');
      setToken(token);
      setIsVerified(true);
      if (onVerify) onVerify(token, null);
    } catch (err) {
      setIsVerified(false);
      setToken(null);
      if (onVerify) onVerify(null, 'reCAPTCHA verification failed');
    } finally {
      setIsVerifying(false);
    }
  };

  useEffect(() => {
    // Auto-verify when component mounts and reCAPTCHA is loaded
    if (loaded && executeRecaptcha) {
      handleVerify();
    }
  }, [loaded, executeRecaptcha]);

  if (loadError) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between p-4 bg-error/10 border border-error/20 rounded-xl">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-lg bg-error/20 text-error">
              <AlertCircle className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-medium text-error">reCAPTCHA loading failed</p>
              <p className="text-xs text-muted-foreground">
                Please check your internet connection and reCAPTCHA configuration
              </p>
            </div>
          </div>
          
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-error text-error-foreground rounded-lg text-sm font-medium hover:bg-error/90 transition-all duration-200"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Reload
          </button>
        </div>

        <p className="text-xs text-muted-foreground">
          Make sure your reCAPTCHA site key is valid and your domain is registered in Google reCAPTCHA admin console.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between p-4 bg-background border border-border rounded-xl">
        <div className="flex items-center space-x-3">
          <div className={`p-2 rounded-lg transition-all duration-200 ${
            isVerified 
              ? 'bg-success/10 text-success' 
              : isVerifying 
                ? 'bg-primary/10 text-primary animate-pulse' 
                : 'bg-muted/20 text-muted-foreground'
          }`}>
            {isVerified ? (
              <ShieldCheck className="h-5 w-5" />
            ) : (
              <Shield className="h-5 w-5" />
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">
              {isVerified ? 'Verification complete' : 'Security verification'}
            </p>
            <p className="text-xs text-muted-foreground">
              {isVerified 
                ? 'You have been verified as human' 
                : isVerifying 
                  ? 'Verifying...' 
                  : 'Click to verify you are human'
              }
            </p>
          </div>
        </div>
        
        {!isVerified && (
          <button
            type="button"
            onClick={handleVerify}
            disabled={isVerifying}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            {isVerifying ? 'Verifying...' : 'Verify'}
          </button>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center space-x-2 text-error text-sm animate-shake">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Help text */}
      <p className="text-xs text-muted-foreground">
        This helps us protect against automated abuse. reCAPTCHA v3 runs automatically in the background.
      </p>
    </div>
  );
};

export default Captcha;
